/**
 * @expFlag auth-mobile-redirect-app-main-actions
 * @ticket CLOUDWEB-20237
 * @file
 */

import { SafetyLocalStorage, SafetySessionStorage } from '@mail-core/std/storage';
import { abAuthMobileRedirectAppMainActions } from 'reactApp/appHelpers/featuresHelpers/features/authMobileRedirectAppMainActions';
import type { EButtonLocation } from 'reactApp/appHelpers/toolbarActions/constants';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { renderAllFunctionsInAppModalDialog } from 'reactApp/ui/Mobile/AllFunctionsInAppModal/AllFunctionsInAppModal.helpers';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

import { type RedirectToAppAnalytics, sendRedirectToAppAnalytics } from './redirectToApp.analytics';
import { AUTH_MOBILE_REDIRECT_APP_MAIN_ACTIONS_SHOWN } from './redirectToApp.constants';
import type { RedirectAppMainActions } from './redirectToApp.types';

/**
 * @expFlag promo-in-webview-public
 * @ticket CLOUDWEB-20374
 * @start
 */
const localStorageSafe = new SafetyLocalStorage({});

export const canShowPromo = (storageKey: string): boolean => {
    const lastShownStr = localStorageSafe.getItem(storageKey);
    if (!lastShownStr) {
        return true;
    }
    try {
        const lastShown = parseInt(lastShownStr);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        return now - lastShown > twentyFourHours;
    } catch {
        return true;
    }
};

export const recordPromoShown = (storageKey: string) => {
    localStorageSafe.setItem(storageKey, Date.now().toString());
};
/**
 * @expFlag promo-in-webview-public
 * @ticket CLOUDWEB-20374
 * @end
 */

const sessionStorage = new SafetySessionStorage({});

export const getRedirectAppMainActionsShown = (actionType: RedirectAppMainActions) => {
    return sessionStorage.getItem(`${AUTH_MOBILE_REDIRECT_APP_MAIN_ACTIONS_SHOWN}:${actionType}`);
};

export const setRedirectAppMainActionsShown = (actionType: RedirectAppMainActions) => {
    sessionStorage.setItem(`${AUTH_MOBILE_REDIRECT_APP_MAIN_ACTIONS_SHOWN}:${actionType}`, 'true');
};

const getSourceFrom = ({ isMobileViewer, routeId }: { isMobileViewer: boolean; routeId: string }) => {
    if (isMobileViewer) {
        return 'viewer';
    }

    if (routeId === '/gallery' || routeId === '/') {
        return 'grid';
    }

    return 'folder';
};

export type CreateDwhDataForRedirectToAppProps = {
    buttonLocation: RedirectToAppAnalytics['buttonLocation'];
    eventCategory: RedirectToAppAnalytics['eventCategory'];
    actionType: RedirectAppMainActions;
    action: RedirectToAppAnalytics['action'];
    isMobileViewer?: boolean;
    routeId: string;
    storage: EStorageType;
};

export const createDwhDataForRedirectToApp = ({
    buttonLocation,
    eventCategory,
    actionType,
    action,
    isMobileViewer,
    routeId,
    storage,
}: CreateDwhDataForRedirectToAppProps): RedirectToAppAnalytics => ({
    buttonLocation,
    eventCategory,
    actionButton: actionType,
    action,
    sourceFrom: getSourceFrom({ isMobileViewer: Boolean(isMobileViewer), routeId }),
    productNavigationPlace: storage === EStorageType.gallery ? 'gallery' : 'files',
    trkLink: abAuthMobileRedirectAppMainActions ? abAuthMobileRedirectAppMainActions.href : undefined,
});

type ShowRedirectAppMainActionProps = {
    actionType: RedirectAppMainActions;
    callback: () => void;
    storage: EStorageType;
    buttonLocation: EButtonLocation | null;
    routeId: string;
    isMobileViewer?: boolean;
};

export const showRedirectAppMainAction = ({
    actionType,
    callback,
    storage,
    buttonLocation,
    isMobileViewer,
    routeId,
}: ShowRedirectAppMainActionProps) => {
    if (
        !abAuthMobileRedirectAppMainActions ||
        abAuthMobileRedirectAppMainActions.type === 'd' ||
        getRedirectAppMainActionsShown(actionType)
    ) {
        return callback();
    }

    let href;
    const path = routeId === '/gallery' ? '' : `${routeId.replace(/^\//, '')}`;

    if (storage === EStorageType.home) {
        href = `${abAuthMobileRedirectAppMainActions.href}?route=folders&path=${path}`;
    } else if (storage === EStorageType.gallery) {
        href = `${abAuthMobileRedirectAppMainActions.href}?route=gallery`;
    } else {
        return callback();
    }

    if (abAuthMobileRedirectAppMainActions.type === 'a') {
        renderAllFunctionsInAppModalDialog({ href, actionType, buttonLocation, routeId, storage });
    }

    if (abAuthMobileRedirectAppMainActions.type === 'b') {
        const snackbarTitle = {
            [EActions.createFolder]: 'Создать папку можно в приложении',
            [EActions.download]: 'Скачать можно в приложении',
            [EActions.upload]: 'Загрузить можно в приложении',
        };
        const snackbarId = `mobile-redirect-snackbar-${Date.now()}`;

        snackbarController.showSnackbar({
            id: snackbarId,
            title: snackbarTitle[actionType],
            text: 'Проверяем, есть ли оно у вас',
            type: SnackbarTypes.loading,
            onShow: () => {
                sendRedirectToAppAnalytics(
                    createDwhDataForRedirectToApp({
                        buttonLocation,
                        eventCategory: ECategoryGa.snackActionBlock,
                        actionType,
                        action: 'show',
                        isMobileViewer,
                        routeId,
                        storage,
                    })
                );
            },
        });
        setTimeout(() => {
            setRedirectAppMainActionsShown(actionType);
            snackbarController.hideSnackbar(snackbarId);
            sendRedirectToAppAnalytics(
                createDwhDataForRedirectToApp({
                    buttonLocation,
                    eventCategory: ECategoryGa.snackActionBlock,
                    actionType,
                    action: 'close',
                    isMobileViewer,
                    routeId,
                    storage,
                })
            );

            window.location.href = href;
        }, abAuthMobileRedirectAppMainActions.timeout || 3000);
    }

    if (abAuthMobileRedirectAppMainActions.type === 'c') {
        if (actionType === EActions.download) {
            setRedirectAppMainActionsShown(actionType);
            window.location.href = href;
        } else {
            renderAllFunctionsInAppModalDialog({ href, actionType, buttonLocation, routeId, storage });
        }
    }
};

export const shouldShowDownloadBlockInExp = ({ storage }: { storage?: EStorageType }) => {
    return (
        abAuthMobileRedirectAppMainActions &&
        abAuthMobileRedirectAppMainActions.type !== 'd' &&
        !getRedirectAppMainActionsShown(EActions.upload) &&
        (storage === EStorageType.home || storage === EStorageType.gallery)
    );
};
